/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

import {Injectable, OnDestroy} from '@angular/core';
import 'rxjs/add/operator/map';
import {DataReference} from "../shared/DataReference";
import {EntityFollowing} from "../models/following/EntityFollowing";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {Observable} from "rxjs/Observable";
import {AuthService} from "./auth-service";
import {EventService} from "./event-service";
import {Subject} from "rxjs/Subject";
import {ReplaySubject} from "rxjs/ReplaySubject";
import {Studio} from "../models/Studio";
import {DeviceUtilityService} from "./device-utility-service/device-utility-service";
import {Subscription} from "rxjs/Subscription";
import {FirebaseApp} from "angularfire2";


@Injectable()
export class FollowingService implements OnDestroy {
  ngOnDestroy(): void {
    if (this._pushSubscription) {
      this._pushSubscription.unsubscribe();
    }
  }

  // region Private Observables

  private _entitiesFollowing$: BehaviorSubject<EntityFollowing[]> = new BehaviorSubject<EntityFollowing[]>([]);

  private _pushSubscription: Subscription;

  // endregion

  // region Bindable Observables

  entitiesFollowing$: Observable<EntityFollowing[]> = this._entitiesFollowing$.asObservable();

  // endregion


  constructor(private auth: AuthService,
              private firebase: FirebaseApp,
              private eventService: EventService,
              private deviceService: DeviceUtilityService) {
  }

  // region Session Initializer Methods

  initForUser(userId: string) {
    this.firebase.database().ref(DataReference.getEntitiesFollowingRef(userId)).off(DataReference.Value);

    this.firebase.database().ref(DataReference.getEntitiesFollowingRef(userId)).on(DataReference.Value, snapshot => {
      console.log('following callback');
      if (snapshot.exists() && snapshot.hasChildren()) {
        let entities: EntityFollowing[] = [];
        snapshot.forEach(child => {
          EntityFollowing.fromSnapshotList(child).map(item => {
            entities.push(item);
          });
          return false;
        });
        // console.log(entities);

        this._entitiesFollowing$.next(entities);
      } else {
        this._entitiesFollowing$.next([]);
      }
    });

    this.handlePushTokenChanges(userId);
  }

  // endregion

  // region Public Methods

  updateDancersFollowed(studioKey: string, addDancers: string[], removeDancers: string[]) : Observable<boolean> {

    const subject: Subject<boolean> = new ReplaySubject();

    Observable.combineLatest(
      this.auth.userId,
      this.eventService.purchasedEvents
    )
      .first()
      .subscribe(([userId, purchasedEvents]) => {
        if (!userId) {
          subject.error('User is not logged in');
          subject.complete();
          return;
        }

        // construct data to send
        let data: any = {};

        // user following add
        addDancers
          .map(dancer => {
            return {
              key: `${DataReference.getDancersFollowingRef(userId)}/${this.createUserFollowKey()}`,
              value: {
                studioKey: studioKey,
                name: FollowingService.cleanDancerName(dancer)
              }
            }
          })
          .forEach(item => {
            data[item.key] = item.value;
          });

        // user following remove
        removeDancers
          .map(dancer => {
            this._entitiesFollowing$.getValue()
              .filter(entity =>
                entity.entityType == EntityFollowing.DANCERS &&
                entity.entityId == studioKey &&
                entity.name.toUpperCase() == dancer.toUpperCase()
              )
              .map(entity => entity.id)
              .forEach(dancerId => {
                data[`${DataReference.getDancersFollowingRef(userId)}/${dancerId}`] = null;
              });
          });

        // events
        purchasedEvents.map(event => event.event.id)
          .forEach(eventKey => {
            addDancers
              .map(dancer => DataReference.getEventDancersFollowingRef(eventKey, studioKey, dancer, userId))
              .forEach(key => {
                data[key] = true;
              });

            removeDancers
              .map(dancer => DataReference.getEventDancersFollowingRef(eventKey, studioKey, dancer, userId))
              .forEach(key => {
                data[key] = null;
              });
          });

        console.log('Going to call now to update dancers');
        // we have the data, now do the update
        this.firebase.database().ref().update(data, err => {
          if (err) {
            subject.error('Error following dancer(s)');
            subject.complete();
          } else {
            console.log('Successfully followed dancers');
            subject.next(true);
            subject.complete();
          }
        });

      });

    return subject.asObservable();
  }

  updateStudioFollowing(studio: Studio, shouldFollow: boolean) {
    const subject = new ReplaySubject();

    Observable.combineLatest(
      this.auth.userId,
      this.eventService.purchasedEvents,
      this.deviceService.pushToken$
    )
      .first()
      .subscribe(([userId, purchasedEvents, pushToken]) => {
        if (!userId) {
          subject.error('User is not logged in');
          subject.complete();
          return;
        }

        // construct data to send
        let data: any = {};

        // studio following node
        if (pushToken != DeviceUtilityService.UNKNOWN_TOKEN || !shouldFollow) {
          data[DataReference.getGeneralStudiosFollowingRef(studio.id, userId)] = shouldFollow ? pushToken : null;
        } else {
          console.warn('Token unknown. Could not update token for studio');
        }

        // user following node
        data[`${DataReference.getStudiosFollowingRef(userId)}/${studio.id}`] = shouldFollow ? {
          name: studio.name
        } : null;

        // event following node
        const followValue = shouldFollow ? shouldFollow : null;
        purchasedEvents.map(event => event.event.id)
          .forEach(eventKey => {
            data[DataReference.getEventStudiosFollowingRef(eventKey, studio.id, userId)] = followValue;
          });

        // console.log('This is what we are going to update');
        // console.log(data);

        // subject.next(true);
        // subject.complete();

        // we have the data, now do the update
        this.firebase.database().ref().update(data, err => {
          if (err) {
            subject.error('Error following studio');
            subject.complete();
          } else {
            console.log('Successfully followed studio');
            subject.next(true);
            subject.complete();
          }
        });

      });

    return subject;
  }

  // followDancer(studioKey: string, dancerName: string) : Promise<boolean> {
  //   return new Promise<boolean>((resolve, reject) => {
  //
  //     // check if already following dancer
  //     this.auth.userId.subscribe(userId => {
  //       if (!userId) {
  //         reject('You are not logged in');
  //         return;
  //       }
  //
  //       // check if already following this dancer
  //       if (this.isFollowingDancer(studioKey, dancerName)) {
  //         reject('Dancer is already being followed');
  //         return;
  //       }
  //
  //       // construct data to send
  //       let data = {};
  //
  //       // user following node
  //       data[`${DataReference.getDancersFollowingRef(userId)}/${FollowingService.createUserFollowKey()}`] = {
  //         studioKey: studioKey,
  //         name: FollowingService.cleanDancerName(dancerName)
  //       };
  //
  //       // purchased events node
  //       this.eventService.purchasedEvents
  //         .map(events => events
  //           .map(event => DataReference.getEventDancersFollowingRef(event.event.id, studioKey, dancerName, userId))
  //         )
  //         .first()
  //         .subscribe(eventRefs => {
  //           eventRefs.map(eventRef => {
  //             data[eventRef] = true;
  //
  //             console.log('Update dancers following');
  //             console.log(data);
  //           });
  //         });
  //
  //     });
  //
  //   });
  // }

  // endregion

  // region Private Helper Methods

  private isFollowingDancer(studioKey: string, dancerName: string) : boolean {
    return this._entitiesFollowing$.getValue()
      .filter(entity =>
        entity.entityType == EntityFollowing.DANCERS &&
        entity.entityId == studioKey &&
        entity.name.toUpperCase() == dancerName.toUpperCase()
      )
      .length > 0;
  }

  private createUserFollowKey() : string {
    return this.firebase.database().ref(DataReference.getEntitiesFollowingRef('')).push().key;
  }

  private static cleanDancerName(name: string) : string {
    return name.trim().toUpperCase();
  }

  private handlePushTokenChanges(userId: string) {
    if (this._pushSubscription) {
      this._pushSubscription.unsubscribe();
    }

    this._pushSubscription = Observable.combineLatest(
      this.deviceService.pushToken$.filter(t => !!t),
      this.eventService.purchasedEvents
        .map(event => event.map(e => e.event.id)),
      this.entitiesFollowing$
        .map(entities => entities.filter(e => e.entityType == EntityFollowing.STUDIOS).map(s => s.entityId))
    )
      .subscribe(([pushToken, purchasedEvents, studiosFollowing]) => {
        if (!pushToken || pushToken == DeviceUtilityService.UNKNOWN_TOKEN) {
          console.error('Push token is null');
          return;
        }
        console.log('Push token is good ' + pushToken);

        if (!userId) {
          console.error('User is not logged in');
          return;
        }

        let data = {};

        // user node
        data[DataReference.getUserPushTokenRef(userId)] = pushToken;

        // studio following node
        for (let studioKey of studiosFollowing) {
          data[DataReference.getGeneralStudiosFollowingRef(studioKey, userId)] = pushToken;
        }

        // events node
        for (let eventKey of purchasedEvents) {
          data[DataReference.getEventPushTokenRef(userId, eventKey)] = pushToken;
        }

        console.log('We are going to update tokens');
        console.log(data);

        this.firebase.database().ref().update(data, err => {
          if (err) {
            console.error('Error updating tokens');
            console.error(err);
          } else {
            console.log('Successfully followed dancers');
          }
        });

      });
  }

  // endregion

}



// WEBPACK FOOTER //
// ./src/providers/following-service.ts